import { AsyncForm } from '@components/async-form';
import { BtnPrimary } from '@components/buttons';
import { ErrorPage } from '@components/error-page';
import { IcoCheck } from '@components/icons';
import { defRoute, LoadedProps, RouteLoadProps } from '@components/router';
import { FixedContent, FixedPage } from '@course/components/fixed-page';
import { TopBar } from '@course/components/top-bar';
import { useAuth, useCurrentUser } from 'client/lib/auth';
import { rpx } from 'client/lib/rpx-client';
import { useIntl } from 'shared/intl/use-intl';

type Props = LoadedProps<typeof load>;

export const route = defRoute({
  isPublic: true,
  load,
  Page,
});

function load(route: RouteLoadProps) {
  return rpx.auth.getOneClickUser({
    resetCode: route.params.code,
  });
}

function Page(props: Props) {
  const intl = useIntl();
  const user = useCurrentUser();
  const isError = !user && !props.state?.email;

  if (isError) {
    return <ErrorPage title={intl('Wrong or expired login code.')} />;
  }

  return <ThankYouValidateEmail {...props} />;
}

function performRedirect(props: Props) {
  const { redirect } = props.params;
  props.router.goto(redirect || '/');
}

function ThankYouValidateEmail(props: Props) {
  const intl = useIntl();
  const auth = useAuth();

  return (
    <FixedPage title={intl(`Thank you`)}>
      <FixedContent class="bg-white">
        <TopBar />

        <AsyncForm
          class=" m-xl flex flex-col gap-6"
          onSubmit={async () => {
            await auth.login(
              rpx.auth.oneClickAutoLogin({
                resetCode: props.params.code,
              }),
            );
            performRedirect(props);
          }}
        >
          <div className="flex flex-col items-center mt-16 text-center px-4">
            <div className="rounded-full bg-green-100 p-6 mb-6">
              <IcoCheck />
            </div>

            <h2 className="text-2xl font-semibold text-gray-900">Email Verified Successfully!</h2>
            <p className="mt-2 text-gray-600">
              Your email address has been verified. You can now send messages and previews to your
              students.
            </p>
            <BtnPrimary class="mt-3">{intl(`Return to courses`)}</BtnPrimary>
          </div>
        </AsyncForm>
      </FixedContent>
    </FixedPage>
  );
}
